import React, { useState, useEffect, ReactElement } from "react";
import { Snackbar, IconButton } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomButton from "components/common/Button";

import "./Snackbar.scss";

export enum AlertType {
  success = "success",
  error = "error",
  info = "info",
}

interface CallToAction {
  onClick?: () => void;
  text?: string;
}

interface CustomSnackbarProps {
  message?: string;
  label?: string;
  duration?: number;
  onClose?: () => void;
  type?: AlertType;
  open?: boolean;
  snackbarMessageContent?: ReactElement | null;
  prefixIcon?: ReactElement | null;
  callToAction?: CallToAction;
}

export default function CustomSnackbar({
  message,
  label = "",
  duration = 3000,
  onClose = () => {},
  type = AlertType.success,
  open = false,
  snackbarMessageContent = null,
  prefixIcon = <CheckIcon className="snackbar-icon" />,
  callToAction = {},
}: CustomSnackbarProps) {
  const [isVisible, setIsVisible] = useState<boolean>(open);

  useEffect(() => {
    setIsVisible(open);
  }, [open]);

  const handleClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setIsVisible(false);
    onClose();
  };

  const renderCallToAction = () => {
    const { onClick = null, text = "" } = callToAction;

    if (onClick) {
      return (
        <CustomButton
          variant="text"
          size="small"
          className="snackbar-call-to-action-link"
          color="inherit-white"
          onClick={onClick}
          label={text}
          disableRipple
          disableHoverEffect
        />
      );
    }
    return null;
  };

  const snackbarClass = type === AlertType.error ? "snackbar-error" : "snackbar-success";

  const snackbarContent = (
    <div className={`snackbar box-shadow-lg flex justify-between items-center px-1 ${snackbarClass}`}>
      <div className="snackbar-content gap-05">
        {type === AlertType.error ? (
          <InfoOutlinedIcon className="snackbar-alert-icon" />
        ) : (
          (prefixIcon ?? <CheckIcon className="snackbar-icon" />)
        )}
        {message ? (
          <span className={`${type === AlertType.error ? "alert-description" : "para-body-m-regular"}`}>{message}</span>
        ) : (
          snackbarMessageContent
        )}
        {renderCallToAction()}
      </div>
      <div className="snackbar-actions gap-05">
        <span className="button-small">{label}</span>
        <IconButton onClick={handleClose} className="snackbar-close-button" aria-label="close" size="small">
          <CloseIcon className={`${type === AlertType.error ? "snackbar-alert-close-icon" : "snackbar-close-icon"}`} />
        </IconButton>
      </div>
    </div>
  );

  return (
    <Snackbar
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      open={isVisible}
      autoHideDuration={duration}
      onClose={handleClose}
      className="snackbar-container"
    >
      {snackbarContent}
    </Snackbar>
  );
}
